import React, { FC } from 'react';
import { useToast } from '@common/toast';
import Button from '@components/common/button';
import Tooltip from '@components/common/tooltip';
import { JDModalType } from '@components/position/jd/apply/modals';
import { PositionDetailVO } from '@domain/vo/position/jd/PositionDetailVO';
import { JobApplicationProcessType } from '@domain/constant/jobApplicationProcessType';
import { PositionApplyEnum } from '@domain/constant/PositionApplyType';
import useMessageModal from '@utils/hooks/useMessageModal';

import DateFormat from '@utils/date/format';
import DateUtil from '@utils/date/util';
import classnames from 'classnames/bind';
import styles from './applyButton.module.scss';
const cx = classnames.bind(styles);

const ToolTip = () => {
  return (
    <div className={cx('tooltip')}>
      <div className={cx('tooltipBox')}>
        전형이 진행 된 경우나 지원 마감 시에는
        <br />
        취소가 불가능 합니다.
      </div>
    </div>
  );
};

interface CMSApplyProps {
  recruitmentUrl: string;
}

//CMS 수동 포지션 공고용 지원 하기 버튼
const CMSApply: FC<CMSApplyProps> = ({ recruitmentUrl }) => {
  if (!recruitmentUrl) return null;
  return (
    <a className={cx('btn', 'apply')} target="_blank" href={recruitmentUrl} rel="noreferrer">
      홈페이지 지원하기
    </a>
  );
};

interface JobFlexApplyProps {
  isStart?: boolean;
  startDateTime?: string;
  handleApply: () => void;
}

const JobFlexApply: FC<JobFlexApplyProps> = ({ isStart, startDateTime, handleApply }) => {
  return (
    <a className={cx('btn', 'apply', 'jobflex', { disabled: !isStart })} role="button" onClick={handleApply}>
      {isStart ? '홈페이지 지원하기' : startDateTime}
    </a>
  );
};

const MatchingApply = ({ handleApply }: { handleApply: () => void }) => (
  <Tooltip
    label={
      <Button className={cx('matching')} onClick={handleApply}>
        MATCHING 수락하기
      </Button>
    }
    content={`잡다매칭을 통해 제안받은 포지션입니다.\n이 포지션에 지원하면 인사담당자가 우선 검토해요.`}
    placement="bottom-center"
    theme="gray"
    top={56}
  />
);

const MatchingClosed = () => <Button disabled>MATCHING 수락 완료</Button>;

const Apply = ({ handleApply }: { handleApply: () => void }) => {
  return (
    <a className={cx('btn', 'apply')} role="button" onClick={handleApply}>
      지원하기
    </a>
  );
};

const Cancel = ({ handleCancel }: { handleCancel: () => void }) => {
  return (
    <div className={cx('btnWrap')}>
      <a className={cx('btn', 'apply', 'cancel')} role="button" onClick={handleCancel}>
        지원 취소
      </a>
      <ToolTip />
    </div>
  );
};

const DisabledCancel = () => {
  return (
    <div className={cx('btnWrap')}>
      <a className={cx('btn', 'apply', 'cancel', 'disabled')} role="button">
        지원 취소
      </a>
      <ToolTip />
    </div>
  );
};

const Closed = () => {
  return (
    <div className={cx('btnWrap')}>
      <button className={cx('btn')} type="button" disabled>
        지원 마감
      </button>
    </div>
  );
};

interface BeforeStartProps {
  openingDateTime: string;
}

const BeforeStart = ({ openingDateTime }: BeforeStartProps) => {
  const openingDateTimeText = DateFormat.format(new Date(openingDateTime), 'MM.dd(week) HH:mm');
  return (
    <div className={cx('btnWrap')}>
      <button className={cx('btn')} type="button" disabled>
        {openingDateTimeText} OPEN
      </button>
    </div>
  );
};

interface ApplyButtonProps {
  positionSn?: number;
  positionDetail: PositionDetailVO;
  applyState: JobApplicationProcessType | null;
  setModalState: (modalState: JDModalType) => void;
  handleApply: () => Promise<void>;
}

const ApplyButton = ({ positionSn, positionDetail, applyState, setModalState, handleApply }: ApplyButtonProps) => {
  const { basicInfo, externalInfo, stateInfo } = positionDetail;
  const { openingDateTime } = basicInfo;
  const matchingOfferStatus = stateInfo?.matchingOfferStatus;
  const isInDecisionDueDate = stateInfo?.isInDecisionDueDate;
  const { openMessageModal } = useMessageModal();

  const Toast = useToast();

  const isBeforeStarted =
    basicInfo.openingDateTime && DateUtil.compare(new Date(), new Date(basicInfo.openingDateTime)) === -1;
  const isClosed =
    basicInfo.closingDateTime && DateUtil.compare(new Date(basicInfo.closingDateTime), new Date()) === -1;

  const handleJobFlexApply = async () => {
    if (!externalInfo?.isStart) return;

    if (!externalInfo || !externalInfo.url) {
      setModalState('ERROR');
      return;
    }
    const { url } = externalInfo;
    window.open(url);
  };

  const handleCancel = async () => {
    if (!positionSn) return null;
    //포지션 마감, 종료 여부 확인
    const { terminatePosition } = basicInfo;
    if (terminatePosition) {
      Toast({
        size: 'xlarge',
        type: 'error',
        iconType: 'info',
        content: <>전형이 진행 된 경우나 지원 마감 시에는 취소가 불가능합니다.</>,
      });
      return;
    }

    setModalState('CANCEL');
  };

  const getButtonEl = () => {
    // 매칭 제안 받은 경우
    if (isInDecisionDueDate) return <MatchingApply handleApply={() => openMessageModal(positionSn)} />;
    // 매칭 제안 수락한 경우
    if (matchingOfferStatus === 'ACCEPTED') return <MatchingClosed />;

    // 마감된 포지션 && 종료된 포지션
    if (!basicInfo.openYn || isClosed) return <Closed />;

    //CMS 수동 공고 일 경우
    if (basicInfo.applyType === PositionApplyEnum.CMS) {
      return <CMSApply recruitmentUrl={basicInfo.recruitmentUrl} />;
    } else if (basicInfo.externalYn && externalInfo) {
      return (
        <JobFlexApply
          isStart={externalInfo?.isStart}
          startDateTime={externalInfo?.startDateTime}
          handleApply={handleJobFlexApply}
        />
      );
    }

    switch (applyState) {
      case 'APPLY': // 지원
        return <Cancel handleCancel={handleCancel} />;
      case 'INTERVIEW_OFFERED': // 면접 제안
      case 'INTERVIEW_FAILED': // 불합격
      case 'ACC_REQUEST': //역검 제출 요청
      case 'ACC_SUBMIT': //역검 제출 완료
      case 'PHS_REQUEST': //phs 제출 요청
      case 'PHS_SUBMIT': //phs 제출완료
      case 'INTERVIEW_ACCEPTED': // 면접 제안 수락
        return <DisabledCancel />;
      case 'INTERVIEW_DENIED': // 면접 제안 거절
      case 'INTERVIEW_PASSED': // 합격
        return <Closed />;
    }

    //예외 : 지원자가 지원하고 시작일이 미래로 변경됬을 경우, 지원취소 버튼 출력
    if (isBeforeStarted) return <BeforeStart openingDateTime={openingDateTime ?? ''} />;

    return <Apply handleApply={handleApply} />;
  };

  return <>{getButtonEl()}</>;
};
export default ApplyButton;
